import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AxiosResponse } from "axios";
import { FiArrowLeft, FiSearch, FiUser, FiPlus, FiX, FiUsers, FiCheckCircle } from "react-icons/fi";
import Sidebar from "../Sidebar/Sidebar";
import { SmsApis } from "../../apis/smsApis";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const initials = (first: string, last: string) =>
  `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase() || "?";

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-600",
  "bg-purple-100 text-purple-600",
  "bg-green-100 text-green-700",
  "bg-amber-100 text-amber-600",
  "bg-rose-100 text-rose-600",
];
const avatarColor = (id: number) => AVATAR_COLORS[id % AVATAR_COLORS.length];

export default function CreateContactGroup() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({ name: "", description: "", contacts: [] as number[] });
  const [availableContacts, setAvailableContacts] = useState<any[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    fetchContacts();
    if (isEditMode) fetchGroupDetails();
  }, [id]);

  const fetchGroupDetails = () => {
    setIsFetching(true);
    SmsApis.getGroup(Number(id))
      .then((res: AxiosResponse<any>) => {
        if (res?.data) {
          setFormData({
            name: res.data.name,
            description: res.data.description || "",
            contacts: res.data.contacts.map((c: any) => c.id),
          });
          setSelectedContacts(res.data.contacts);
        }
      })
      .catch(() => { toast.error("Failed to load group details"); navigate("/contact-groups"); })
      .finally(() => setIsFetching(false));
  };

  const fetchContacts = (term = searchTerm) => {
    SmsApis.getContacts(100, term)
      .then((res: AxiosResponse<any>) => { if (res?.data) setAvailableContacts(res.data.data); })
      .catch(() => toast.error("Failed to load contacts"));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    fetchContacts(e.target.value);
  };

  const handleContactSelect = (contact: any) => {
    if (!formData.contacts.includes(contact.id)) {
      setFormData({ ...formData, contacts: [...formData.contacts, contact.id] });
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  const handleContactRemove = (contactId: number) => {
    setFormData({ ...formData, contacts: formData.contacts.filter((i) => i !== contactId) });
    setSelectedContacts(selectedContacts.filter((c) => c.id !== contactId));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error("Group name is required"); return; }
    setIsLoading(true);
    const apiCall = isEditMode ? SmsApis.updateGroup(Number(id), formData) : SmsApis.createGroup(formData);
    apiCall
      .then(() => {
        toast.success(`Group ${isEditMode ? "updated" : "created"} successfully`);
        setTimeout(() => navigate("/contact-groups"), 1500);
      })
      .catch((err: any) => toast.error(err.response?.data?.message || `Failed to ${isEditMode ? "update" : "create"} group`))
      .finally(() => setIsLoading(false));
  };

  if (isFetching) {
    return (
      <>
        <Sidebar title={`${isEditMode ? "Edit" : "Create"} Contact Group`} />
        <div className="relative md:ml-64 bg-gray-50 min-h-screen flex items-center justify-center">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-[#0071BC] animate-spin" />
        </div>
      </>
    );
  }

  const unselected = availableContacts.filter((c) => !formData.contacts.includes(c.id));

  return (
    <>
      <Sidebar title={`${isEditMode ? "Edit" : "Create"} Contact Group`} />
      <div className="relative md:ml-64 bg-gray-50 min-h-screen pb-12">

        <div className="px-4 sm:px-6 pt-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              {/* ── Left: Group details ───────────────────────────── */}
              <div className="space-y-5">
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 space-y-4">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Group Details</p>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">
                      Group Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g. VIP Customers"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Enter a description for this group…"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all leading-relaxed"
                    />
                  </div>
                </div>

                {/* Selected contacts */}
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Selected Contacts</p>
                    {selectedContacts.length > 0 && (
                      <span className="text-xs font-bold text-[#0071BC] bg-blue-50 px-2.5 py-1 rounded-full">
                        {selectedContacts.length}
                      </span>
                    )}
                  </div>

                  {selectedContacts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div className="w-10 h-10 rounded-2xl bg-gray-50 flex items-center justify-center mb-2">
                        <FiUsers className="text-gray-300" size={18} />
                      </div>
                      <p className="text-xs text-gray-400">No contacts selected yet</p>
                      <p className="text-xs text-gray-300 mt-0.5">Pick contacts from the panel →</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-52 overflow-y-auto">
                      {selectedContacts.map((contact) => (
                        <div key={contact.id} className="flex items-center gap-3 bg-[#0071BC]/5 border border-[#0071BC]/10 rounded-2xl px-3 py-2.5">
                          <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarColor(contact.id)}`}>
                            {initials(contact.first_name, contact.last_name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{contact.first_name} {contact.last_name}</p>
                            <p className="text-xs text-gray-400 truncate">{contact.phone}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleContactRemove(contact.id)}
                            className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 transition-all flex-shrink-0"
                          >
                            <FiX size={12} strokeWidth={2.5} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/contact-groups")}
                    className="flex-1 py-3.5 border border-gray-200 text-gray-500 text-sm font-bold rounded-2xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 py-3.5 bg-[#0071BC] text-white text-sm font-extrabold rounded-2xl hover:bg-[#005fa0] active:scale-[0.97] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> {isEditMode ? "Updating…" : "Creating…"}</>
                    ) : (
                      <><FiCheckCircle size={15} /> {isEditMode ? "Update Group" : "Create Group"}</>
                    )}
                  </button>
                </div>
              </div>

              {/* ── Right: Contact picker ─────────────────────────── */}
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 flex flex-col h-fit lg:sticky lg:top-24">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Add Contacts</p>

                {/* Search */}
                <div className="relative mb-4">
                  <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                  <input
                    type="text"
                    placeholder="Search contacts…"
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all"
                  />
                </div>

                {/* Available contacts list */}
                <div className="overflow-y-auto max-h-[420px] space-y-1.5">
                  {unselected.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-10 h-10 rounded-2xl bg-gray-50 flex items-center justify-center mb-2">
                        <FiUser className="text-gray-300" size={18} />
                      </div>
                      <p className="text-xs text-gray-400">{searchTerm ? "No contacts match your search" : "No contacts available"}</p>
                    </div>
                  ) : (
                    unselected.map((contact) => (
                      <button
                        type="button"
                        key={contact.id}
                        onClick={() => handleContactSelect(contact)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-gray-50 border border-transparent hover:border-gray-100 transition-all group text-left"
                      >
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarColor(contact.id)}`}>
                          {initials(contact.first_name, contact.last_name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800">{contact.first_name} {contact.last_name}</p>
                          <p className="text-xs text-gray-400">{contact.phone}</p>
                        </div>
                        <div className="w-7 h-7 rounded-xl bg-[#0071BC]/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <FiPlus className="text-[#0071BC]" size={13} />
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {unselected.length > 0 && (
                  <p className="text-xs text-gray-300 text-center mt-3">{unselected.length} contact{unselected.length !== 1 ? "s" : ""} available</p>
                )}
              </div>

            </div>
          </form>
        </div>
      </div>

      <ToastContainer position="bottom-right" autoClose={2500} hideProgressBar theme="light" toastStyle={{ borderRadius: "12px" }} />
    </>
  );
}
